import React, { useState, useMemo } from 'react';
import { Package, Plus, Edit, Trash2, Search, AlertTriangle } from 'lucide-react';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';
import OnderdeelEditor from '../components/OnderdeelEditor';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
const eur = (n) => (n || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Onderdelen() {

  const { t } = useT();  const { onderdelen, deleteOnderdeel } = useData();
  const [filter, setFilter] = useState('');
  const [showLow, setShowLow] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorItem, setEditorItem] = useState(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return onderdelen.filter(o => (!f || (o.code || '').toLowerCase().includes(f) || (o.omschrijving || '').toLowerCase().includes(f) || (o.locatie || '').toLowerCase().includes(f)) && (!showLow || (Number(o.voorraad) || 0) <= (Number(o.minVoorraad) || 0)));
  }, [onderdelen, filter, showLow]);

  const selected = onderdelen.find(o => o.id === selectedId);
  const totaalWaarde = onderdelen.reduce((s, o) => s + (Number(o.voorraad) || 0) * (Number(o.inkoop) || 0), 0);
  const lowStock = onderdelen.filter(o => (Number(o.voorraad) || 0) <= (Number(o.minVoorraad) || 0)).length;

  const handleNew = () => { setEditorItem(null); setEditorOpen(true); };
  const handleEdit = (it) => { setEditorItem(it || selected); setEditorOpen(true); };
  const handleDelete = async () => {
    if (!selected) return;
    if (!(await confirmDialog(`${t('Onderdeel verwijderen?', 'Onderdeel verwijderen?')} (${selected.code})`))) return;
    try { await deleteOnderdeel(selected.id); toast.success(t('Onderdeel verwijderd')); setSelectedId(null); }
    catch { toast.error(t('Verwijderen mislukt')); }
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.parts.title')}</h2><p>{onderdelen.length} {t('artikelen', 'artikelen')} · {lowStock} {t('onder minimum voorraad', 'onder minimum voorraad')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn primary" onClick={handleNew}><Plus size={14} /> {t('Toevoegen')}</button>
          <button className="cs-btn" onClick={() => handleEdit()} disabled={!selected}><Edit size={14} /> {t('Bewerken')}</button>
          <button className="cs-btn danger" onClick={handleDelete} disabled={!selected}><Trash2 size={14} /> {t('Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi"><div className="label">{t('Aantal artikelen', 'Aantal artikelen')}</div><div className="value">{onderdelen.length}</div></div>
        <div className="cs-kpi"><div className="label">{t('Voorraadwaarde', 'Voorraadwaarde')}</div><div className="value">{eur(totaalWaarde)}</div></div>
        <div className="cs-kpi"><div className="label">{t('Onder minimum', 'Onder minimum')}</div><div className="value" style={{ color: 'var(--cs-red)' }}>{lowStock}</div></div>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoek code, omschrijving of locatie...', 'Zoek code, omschrijving of locatie...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={showLow} onChange={e => setShowLow(e.target.checked)} /> {t('Alleen lage voorraad', 'Alleen lage voorraad')}
        </label>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr><th>{t('Code')}</th><th>{t('Omschrijving')}</th><th>{t('Locatie')}</th><th style={{textAlign:'right'}}>{t('Voorraad')}</th><th style={{textAlign:'right'}}>{t('Min')}</th><th style={{textAlign:'right'}}>{t('Inkoop')}</th><th style={{textAlign:'right'}}>{t('Verkoop')}</th><th>{t('Status')}</th></tr></thead>
          <tbody>
            {filtered.map(o => (
              <tr key={o.id} className={o.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(o.id)} onDoubleClick={() => handleEdit(o)}>
                <td data-label={t('Code')} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{o.code}</td>
                <td data-label={t('Omschrijving')}>{o.omschrijving}</td>
                <td data-label={t('Locatie')}><span className="cs-badge gray">{o.locatie}</span></td>
                <td data-label={t('Voorraad')} style={{ textAlign: 'right', fontWeight: 600, color: o.voorraad <= o.minVoorraad ? 'var(--cs-red)' : 'inherit' }}>{o.voorraad}</td>
                <td data-label={t('Min')} style={{ textAlign: 'right', color: 'var(--cs-text-muted)' }}>{o.minVoorraad}</td>
                <td data-label={t('Inkoop')} style={{ textAlign: 'right' }}>{eur(o.inkoop)}</td>
                <td data-label={t('Verkoop')} style={{ textAlign: 'right', fontWeight: 600 }}>{eur(o.verkoop)}</td>
                <td data-label={t('Status')}>{o.voorraad <= o.minVoorraad ? <span className="cs-badge red"><AlertTriangle size={10} style={{ verticalAlign: 'middle' }} /> {t('Bestellen', 'Bestellen')}</span> : <span className="cs-badge green">{t('OK','OK')}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="cs-empty">{t('Geen onderdelen gevonden.', 'Geen onderdelen gevonden.')}</div>}
      </div>

      {editorOpen && <OnderdeelEditor onderdeel={editorItem} onClose={() => { setEditorOpen(false); setEditorItem(null); }} />}
    </div>
  );
}
